import CategoryIcon from '@/components/cashflow/CategoryIcon';
import InstitutionLogo from '@/components/cashflow/InstitutionLogo';
import { StringGroupOption } from '@/components/common/FancySelectInput';
import { FlexRow } from '@/components/common/Flex';
import { Text } from '@chakra-ui/react';
import {
    PartnerInfo,
    TransactionEntryCategoryFieldsFragment,
} from '@common/graphql/generated';
import {
    buildBaseCategoryGroups,
    CashflowFinancialEntity,
} from '@common/react/cashflow';
import { DateRange } from '@common/types';
import { sortBy } from '@common/util/arrays';
import { dt, getRangeUnit } from '@common/util/dateTime';
import { TickUnit } from '@common/valueSeries';

export const buildCategoryGroups = (
    categories: TransactionEntryCategoryFieldsFragment[]
) => {
    const categoryGroups: Record<string, StringGroupOption> = {};
    const supercategoryGroups: Record<string, StringGroupOption> = {};

    const baseGroups = buildBaseCategoryGroups(categories);

    for (const [key, value] of Object.entries(baseGroups.categoryGroups)) {
        categoryGroups[key] = {
            label: value.label,
            parent: value.parent,
            options: value.transactionEntryCategories.map((category) => ({
                label: (
                    <FlexRow alignItems="center">
                        <CategoryIcon category={category} />
                        <Text ml={2}>{category.name}</Text>
                    </FlexRow>
                ),
                value: category.id,
            })),
        };
    }
    for (const [key, value] of Object.entries(baseGroups.supercategoryGroups)) {
        supercategoryGroups[key] = {
            label: value.label,
            options: value.transactionEntryCategories.map((category) => ({
                label: (
                    <FlexRow alignItems="center">
                        <CategoryIcon category={category} />
                        <Text ml={2}>{category.name}</Text>
                    </FlexRow>
                ),
                value: category.id,
            })),
        };
    }

    return { categoryGroups, supercategoryGroups };
};

export const buildAccountOptions = (
    financialEntities: CashflowFinancialEntity[],
    partnerInfo: PartnerInfo | null | undefined
) => {
    const anyPartnerAccounts = financialEntities.some((fe) => !fe.isMine);

    const buildOption = (financialEntity: CashflowFinancialEntity) => ({
        label: (
            <FlexRow alignItems="center" gap={2}>
                <InstitutionLogo financialEntity={financialEntity} />
                <Text>{financialEntity.name}</Text>
            </FlexRow>
        ),
        value: financialEntity.id,
    });

    // If there's a partner with at least one shared account, group by the FEs by mine vs. partner
    if (partnerInfo && anyPartnerAccounts) {
        const grouped: Record<string, StringGroupOption> = {};

        sortBy(financialEntities, 'name').forEach((financialEntity) => {
            const key = financialEntity.isMine ? 'mine' : 'partner';

            if (!grouped[key]) {
                grouped[key] = {
                    label:
                        key === 'mine'
                            ? 'My accounts'
                            : `${
                                  partnerInfo.preferredName ||
                                  partnerInfo.firstName
                              }'s accounts`,
                    options: [],
                };
            }

            grouped[key].options.push(buildOption(financialEntity));
        });

        return Object.values(grouped);
    } else {
        // Otherwise, just return a flat list of FEs
        return financialEntities.map(buildOption);
    }
};

export const getTooltipDate = (date: string, unit: TickUnit) =>
    dt(date).isSame(dt(), 'day')
        ? 'Today'
        : dt(date).format(unit === 'day' ? 'ddd, MMM DD, YYYY' : 'MMM, YYYY');

export const getDateRangeText = (dateRange: DateRange) => {
    const startDate = dt(dateRange.startDate);
    const endDate = dt(dateRange.endDate);

    const diffUnit = getRangeUnit(dateRange);

    if (diffUnit === 'month') {
        return startDate.format('MMM YYYY');
    } else if (diffUnit === 'year') {
        return startDate.format('YYYY');
    } else if (diffUnit === 'quarter') {
        return `${startDate.format('MMM')} - ${endDate.format('MMM YYYY')}`;
    } else {
        // Custom date range
        if (startDate.toDateStr() === endDate.toDateStr()) {
            // Single day
            return startDate.format('MMM DD, YYYY');
        } else if (
            startDate.month() === endDate.month() &&
            startDate.year() === endDate.year()
        ) {
            // Start date/end date are in the same month/year
            return `${startDate.format('MMM D')} - ${endDate.format('D, YYYY')}`;
        } else if (startDate.year() === endDate.year()) {
            // Start date/end date are in the same year
            return `${startDate.format('MMM D')} - ${endDate.format('MMM D, YYYY')}`;
        } else {
            // Start date/end date are in different years
            return `${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`;
        }
    }
};
